import {
  Box,
  Card,
  Flex,
  Grid,
  Image,
  Modal,
  Rating,
  Skeleton,
  Stack,
  Text,
  Title,
  useMantineTheme,
} from '@mantine/core';
import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DummyImage, locationIcon } from '../../assets';
import { useRestaurantCardStyles } from './styles';
import { ROUTES } from '../../config/RoutesName';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { ModalCloseButton } from '../Cart/style';
import { useGetRestaurantBusinessHours } from '../../services/restaurant/restaurant.service';
import { convertTo12HourFormat } from '../../config/Helper';

type Props =
  | {
      type: 'regular';
      id: number;
      imageUrl: string;
      name: string;
      averageRating: number;
      averageRatingCount: number;
      openingTime: string;
      closingTime: string;
      schedule: string;
      fullAddress: string;
      restaurantOpen: boolean;
      backgroundImage: string;
    }
  | {
      type: 'loading';
    };

const RestaurantCard: FC<Props> = (props) => {
  const mantinetheme = useMantineTheme();
  const dektop = useMediaQuery(`(min-width: ${mantinetheme.breakpoints.sm})`);
  const { classes, cx } = useRestaurantCardStyles();
  const navigate = useNavigate();
  const { classes: closeButtonStyle } = ModalCloseButton();
  const [opened, { open, close }] = useDisclosure(false);
  const [restaurantId, setRestaurantId] = useState<number | null>(null);

  const { data: restaurantBusinessHourData, isLoading } = useGetRestaurantBusinessHours({
    enableIf: restaurantId !== undefined && restaurantId !== null,
    restaurantId: JSON.stringify(restaurantId!),
  });

  const onClickHandler = () => {
    if (props.type === 'loading') return;
    else {
      navigate(`${ROUTES.DETAILS}/${props.id}`, {
        state: {
          restaurantInfo: {
            restaurantId: props.id,
            restaurantName: props.name,
            rating: Number(props.averageRating),
            ratingCount: props.averageRatingCount,
            openingHours: `${props.openingTime} - ${props.closingTime}`,
            fullAddress: props.fullAddress,
            restaurantOpen: props.restaurantOpen,
            backgroundImage: props?.backgroundImage,
          },
        },
      });
    }
  };

  return (
    <>
      <Box className={cx(classes.container)} onClick={onClickHandler}>
        <Grid p={0} m={0}>
          <Grid.Col className={cx(classes.gridCol, classes.imageGridCol)}>
            <Box w={'100%'} h={'100%'}>
              {props.type === 'loading' ? (
                <Skeleton width={'100%'} height={!dektop ? 108 : 120} />
              ) : (
                <Image radius={8} height={!dektop ? 108 : 120} fit='cover' src={props.imageUrl || DummyImage} />
              )}
            </Box>
          </Grid.Col>
          <Grid.Col className={cx(classes.gridCol, classes.detailsGridCol)}>
            <Flex direction='column' gap={!dektop ? 8 : 10}>
              {props.type === 'loading' ? (
                <Skeleton h={16} w={'70%'} />
              ) : (
                <Text className={classes.restaurantName}>{props.name}</Text>
              )}

              <Flex gap={4} align='center'>
                {props.type === 'loading' ? (
                  <Skeleton h={20} w={'55%'} />
                ) : (
                  <>
                    <Rating
                      readOnly
                      fractions={4}
                      value={Number(props.averageRating)}
                      classNames={{ symbolBody: classes.ratingStar }}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(ROUTES.REVIEWS + `/${props.id}`);
                      }}
                    />
                    <Text className={classes.ratingCount}>({props.averageRatingCount})</Text>
                  </>
                )}
              </Flex>

              <Flex gap={4}>
                {props.type === 'loading' ? (
                  <Skeleton h={13} w={'85%'} />
                ) : (
                  <>
                    {props.restaurantOpen ? (
                      <>
                        <Text className={classes.openingTimingsText}>We&apos;re open :</Text>
                        <Text
                          className={classes.openingTimingsTiming}
                          onClick={(e) => {
                            e.stopPropagation(), setRestaurantId(props.id), open();
                          }}>
                          {props.openingTime} - {props.closingTime}
                        </Text>
                      </>
                    ) : (
                      <Text
                        c='magenta.9'
                        fz={12}
                        fw={500}
                        lh={1}
                        onClick={(e) => {
                          e.stopPropagation(), setRestaurantId(props.id), open();
                        }}>
                        Closed
                      </Text>
                    )}
                  </>
                )}
              </Flex>

              {props.type === 'loading' ? (
                <Skeleton h={13} w={'20%'} />
              ) : (
                <>
                  <Text c='dimmed' fz={13} fw={500} lh={1}>
                    {props.schedule}
                  </Text>
                </>
              )}

              <Flex align='center' gap={4}>
                {props.type === 'loading' ? (
                  <Skeleton h={14} w={'90%'} />
                ) : (
                  <>
                    <Image src={locationIcon} width={14} height={14} fit='cover' />
                    <Text fz={13} lineClamp={1} lh={1}>
                      {props.fullAddress}
                    </Text>
                  </>
                )}
              </Flex>
            </Flex>
          </Grid.Col>
        </Grid>
      </Box>

      <Modal opened={opened} onClose={close} title='Business Hours' centered className={closeButtonStyle.closeButton}>
        <Stack spacing={10} align='center' justify='center'>
          {/* {!isLoading &&
            restaurantBusinessHourData?.data.map((d: any, index: number) => (
              <Card key={index} w={'100%'} shadow='sm'>
                <Text fz={18} fw={500}>
                  {d?.day?.name}
                </Text>
                <Flex gap={4}>
                  {d?.shifts.map((shift: any, index: number) => (
                    <Text key={index} fz={14} fw={450}>
                      {shift?.name}: {shift?.start_time} - {shift?.end_time}
                    </Text>
                  ))}
                </Flex>
              </Card>
            ))} */}
          {isLoading &&
            Array(7)
              .fill('')
              .map((_) => (
                <Flex justify='space-between' align='flex-start' mb={15} w='100%'>
                  <Skeleton h={13} w={75} />
                  <Flex direction='column'>
                    <Skeleton h={13} w={150} />
                  </Flex>
                </Flex>
              ))}
          {!isLoading &&
            restaurantBusinessHourData?.data.map((d: any, index: number) => (
              // <Card key={index} w={'100%'} shadow='sm'>
              //   <Text fz={18} fw={500}>
              //     {d?.day?.name}
              //   </Text>
              //   <Flex gap={4}>
              //     {d?.shifts.map((shift: any, index: number) => (
              //       <Text key={index} fz={14} fw={450}>
              //         {shift?.name}: {shift?.start_time} - {shift?.end_time}
              //       </Text>
              //     ))}
              //   </Flex>
              // </Card>
              <Flex justify='space-between' align='flex-start' mb={10} w='100%'>
                <Text fw='bold' w={50}>
                  {d?.day?.name}
                </Text>
                <Flex direction='column'>
                  {!d?.shifts?.length && (
                    <Text c='#D61D4F' fw='bold' ml={10}>
                      Closed Now
                    </Text>
                  )}
                  {!!d?.shifts?.length &&
                    d?.shifts.map((shift: any, index: number) => (
                      <Text ta='right'>
                        {convertTo12HourFormat(shift?.start_time)} - {convertTo12HourFormat(shift?.end_time)}
                      </Text>
                    ))}
                </Flex>
              </Flex>
            ))}
        </Stack>
      </Modal>
    </>
  );
};

export default RestaurantCard;
